import type { ProfileShip } from '../account/profile-ship';

export const PARTICIPANTS_PER_TEAM = 5;

export const TEAMS = [0, 1] as const;

export const LOBBY_CAPACITY = PARTICIPANTS_PER_TEAM * TEAMS.length;

export const PLACEHOLDER_DIFFICULTY_TIERS = ['Recruit', 'Regular', 'Veteran', 'Elite'] as const;

export type PlaceholderDifficulty = (typeof PLACEHOLDER_DIFFICULTY_TIERS)[number];

export const DEFAULT_PLACEHOLDER_DIFFICULTY: PlaceholderDifficulty = 'Regular';

export const LOBBY_JOIN_QUERY_PARAM = 'join';

export const LOBBY_SPECTATE_QUERY_PARAM = 'watch';

export const JOIN_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRTUVWXY';

export const JOIN_CODE_LENGTH = 6;

export const MAX_LOBBY_NAME_LENGTH = 28;

/** Entity id 0 is the blue base, so no player can hold it; the server spends it as "this seat is bot-flown". */
export const NO_PARTICIPANT = 0;

const JOIN_CODE_LOOKALIKES: Readonly<Record<string, string>> = { I: '1', L: '1', O: '0', Z: '2' };

const WHITESPACE = /^\s$/u;

const DISCARDED = /^[\p{Cc}\p{Cf}]$/u;

export type LobbyVisibility = 'public' | 'private';

/** The ship flown in a seat, not the account's profile icon — see `docs/specs/competitive-mode.md`. */
export interface SeatShip {
  readonly shipId: number;
  readonly skinProductId: number;
}

export interface LobbyParticipant {
  readonly participantId: number;
  readonly name: string;
  readonly guest: boolean;
  readonly profileShip: ProfileShip | null;
}

export type SlotOccupant =
  { readonly kind: 'bot' } | { readonly kind: 'player'; readonly player: LobbyParticipant };

export interface LobbySlot {
  readonly slotId: number;
  readonly team: number;
  readonly occupant: SlotOccupant;
  readonly ship: SeatShip;
}

export interface LobbyTeam {
  readonly team: number;
  readonly difficulty: PlaceholderDifficulty;
}

export interface LobbyView {
  readonly joinCode: string;
  readonly name: string;
  readonly hostName: string;
  readonly visibility: LobbyVisibility;
  readonly teams: readonly LobbyTeam[];
  readonly slots: readonly LobbySlot[];
  readonly spectators: readonly LobbyParticipant[];
  readonly hostParticipantId: number;
  readonly started: boolean;
  readonly startInMs: number;
}

export interface LobbyListRow {
  readonly joinCode: string;
  readonly name: string;
  readonly hostName: string;
  readonly hostProfileShip: ProfileShip | null;
  readonly difficulties: readonly PlaceholderDifficulty[];
  readonly humans: number;
  readonly capacity: number;
  readonly spectators: number;
}

const BOT: SlotOccupant = { kind: 'bot' };

const NO_SHIP: SeatShip = { shipId: 0, skinProductId: 0 };

export function botSlots(): readonly LobbySlot[] {
  return TEAMS.flatMap((team) =>
    Array.from({ length: PARTICIPANTS_PER_TEAM }, (_, index) => ({
      slotId: team * PARTICIPANTS_PER_TEAM + index,
      team,
      occupant: BOT,
      ship: NO_SHIP,
    })),
  );
}

export function createLobby(
  fields: Partial<LobbyView> & Pick<LobbyView, 'joinCode' | 'hostName'>,
): LobbyView {
  return {
    name: lobbyNameFallback(fields.hostName),
    visibility: 'public',
    teams: TEAMS.map((team) => ({ team, difficulty: DEFAULT_PLACEHOLDER_DIFFICULTY })),
    slots: botSlots(),
    spectators: [],
    hostParticipantId: NO_PARTICIPANT,
    started: false,
    startInMs: 0,
    ...fields,
  };
}

export function teamSlots(lobby: LobbyView, team: number): readonly LobbySlot[] {
  return lobby.slots.filter((slot) => slot.team === team);
}

export function occupantPlayer(slot: LobbySlot): LobbyParticipant | null {
  return slot.occupant.kind === 'player' ? slot.occupant.player : null;
}

export function humanCount(lobby: LobbyView): number {
  return lobby.slots.filter((slot) => slot.occupant.kind === 'player').length;
}

export function slotOf(lobby: LobbyView, participantId: number): LobbySlot | null {
  if (participantId === NO_PARTICIPANT) return null;
  return lobby.slots.find((slot) => occupantPlayer(slot)?.participantId === participantId) ?? null;
}

export function isSpectating(lobby: LobbyView, participantId: number): boolean {
  if (participantId === NO_PARTICIPANT) return false;
  return lobby.spectators.some((watcher) => watcher.participantId === participantId);
}

export function hostOf(lobby: LobbyView): LobbyParticipant | null {
  const slot = slotOf(lobby, lobby.hostParticipantId);
  if (slot !== null) return occupantPlayer(slot);
  if (lobby.hostParticipantId === NO_PARTICIPANT) return null;
  return (
    lobby.spectators.find((watcher) => watcher.participantId === lobby.hostParticipantId) ?? null
  );
}

export function isHost(lobby: LobbyView, participantId: number): boolean {
  return participantId !== NO_PARTICIPANT && lobby.hostParticipantId === participantId;
}

export function difficultyOf(lobby: LobbyView, team: number): PlaceholderDifficulty {
  return (
    lobby.teams.find((entry) => entry.team === team)?.difficulty ?? DEFAULT_PLACEHOLDER_DIFFICULTY
  );
}

export function rosterFrozen(lobby: LobbyView): boolean {
  return lobby.started || lobby.startInMs > 0;
}

export function startSeconds(lobby: LobbyView): number {
  return Math.ceil(lobby.startInMs / 1000);
}

export function canSetDifficulty(lobby: LobbyView, participantId: number): boolean {
  return isHost(lobby, participantId) && !rosterFrozen(lobby);
}

export function canStart(lobby: LobbyView, participantId: number): boolean {
  return isHost(lobby, participantId) && !rosterFrozen(lobby);
}

export function canCancelStart(lobby: LobbyView, participantId: number): boolean {
  return isHost(lobby, participantId) && !lobby.started && lobby.startInMs > 0;
}

/** The seated half of the arrangement: a spectator already sits nowhere, and the lock ends both directions. */
export function canSpectate(lobby: LobbyView, participantId: number): boolean {
  if (rosterFrozen(lobby) || participantId === NO_PARTICIPANT) return false;
  return slotOf(lobby, participantId) !== null;
}

export function canTakeSlot(lobby: LobbyView, slotId: number, participantId: number): boolean {
  if (rosterFrozen(lobby)) return false;
  const slot = lobby.slots.find((entry) => entry.slotId === slotId);
  if (slot === undefined) return false;
  const sitting = occupantPlayer(slot);
  return sitting === null || sitting.participantId === participantId;
}

function withoutParticipant(
  slots: readonly LobbySlot[],
  participantId: number,
): readonly LobbySlot[] {
  return slots.map((slot) =>
    occupantPlayer(slot)?.participantId === participantId
      ? { ...slot, occupant: BOT, ship: NO_SHIP }
      : slot,
  );
}

export function takeSlot(
  lobby: LobbyView,
  slotId: number,
  player: LobbyParticipant,
  ship: SeatShip = NO_SHIP,
): LobbyView {
  if (!canTakeSlot(lobby, slotId, player.participantId)) return lobby;
  const vacated = withoutParticipant(lobby.slots, player.participantId);
  return {
    ...lobby,
    slots: vacated.map((slot) =>
      slot.slotId === slotId ? { ...slot, occupant: { kind: 'player', player }, ship } : slot,
    ),
    spectators: lobby.spectators.filter(
      (watcher) => watcher.participantId !== player.participantId,
    ),
  };
}

export function leaveSlot(lobby: LobbyView, participantId: number): LobbyView {
  return { ...lobby, slots: withoutParticipant(lobby.slots, participantId) };
}

export function spectate(lobby: LobbyView, player: LobbyParticipant): LobbyView {
  const already = lobby.spectators.some(
    (watcher) => watcher.participantId === player.participantId,
  );
  return {
    ...lobby,
    slots: withoutParticipant(lobby.slots, player.participantId),
    spectators: already ? lobby.spectators : [...lobby.spectators, player],
  };
}

export function stopSpectating(lobby: LobbyView, participantId: number): LobbyView {
  return {
    ...lobby,
    spectators: lobby.spectators.filter((watcher) => watcher.participantId !== participantId),
  };
}

export function setTeamDifficulty(
  lobby: LobbyView,
  team: number,
  difficulty: PlaceholderDifficulty,
): LobbyView {
  if (lobby.started) return lobby;
  return {
    ...lobby,
    teams: lobby.teams.map((entry) => (entry.team === team ? { ...entry, difficulty } : entry)),
  };
}

export function leaveLobby(lobby: LobbyView, participantId: number): LobbyView {
  return stopSpectating(leaveSlot(lobby, participantId), participantId);
}

export function listRowFor(lobby: LobbyView): LobbyListRow {
  return {
    joinCode: lobby.joinCode,
    name: lobby.name,
    hostName: lobby.hostName,
    hostProfileShip: hostOf(lobby)?.profileShip ?? null,
    difficulties: lobby.teams.map((entry) => entry.difficulty),
    humans: humanCount(lobby),
    capacity: lobby.slots.length,
    spectators: lobby.spectators.length,
  };
}

/** Folds exactly as `LobbyName.Sanitise` does server-side; the two have to move together. */
export function normaliseLobbyName(raw: string): string {
  let folded = '';
  let pendingSpace = false;
  for (const character of raw) {
    if (WHITESPACE.test(character)) {
      pendingSpace = folded !== '';
      continue;
    }
    if (DISCARDED.test(character)) continue;
    const width = character.length + (pendingSpace ? 1 : 0);
    if (folded.length + width > MAX_LOBBY_NAME_LENGTH) break;
    if (pendingSpace) {
      folded += ' ';
      pendingSpace = false;
    }
    folded += character;
  }
  return folded;
}

export function lobbyNameFallback(hostName: string): string {
  const host = normaliseLobbyName(hostName);
  return host === '' ? 'Custom lobby' : normaliseLobbyName(`${host}'s lobby`);
}

export function matchesLobbySearch(row: LobbyListRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return [row.name, row.hostName, row.joinCode].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

export function isJoinable(row: LobbyListRow): boolean {
  return row.humans < row.capacity;
}

export function occupancyLabel(row: LobbyListRow): string {
  return `${String(row.humans)}/${String(row.capacity)}`;
}

/** Folds and truncates exactly as `JoinCode.Normalise` does server-side; the two have to move together. */
export function normaliseJoinCode(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .map((character) => JOIN_CODE_LOOKALIKES[character] ?? character)
    .filter((character) => JOIN_CODE_ALPHABET.includes(character))
    .slice(0, JOIN_CODE_LENGTH)
    .join('');
}

export function joinLink(origin: string, joinCode: string, spectate = false): string {
  const link = `${origin}/?${LOBBY_JOIN_QUERY_PARAM}=${normaliseJoinCode(joinCode)}`;
  return spectate ? `${link}&${LOBBY_SPECTATE_QUERY_PARAM}=1` : link;
}

export interface JoinDeepLink {
  readonly code: string;
  readonly spectate: boolean;
  readonly complete: boolean;
}

export function joinDeepLinkFromQuery(query: URLSearchParams): JoinDeepLink | null {
  const raw = query.get(LOBBY_JOIN_QUERY_PARAM);
  if (raw === null) return null;
  const code = normaliseJoinCode(raw);
  return {
    code,
    spectate: flagIsSet(query.get(LOBBY_SPECTATE_QUERY_PARAM)),
    complete: code.length === JOIN_CODE_LENGTH,
  };
}

function flagIsSet(raw: string | null): boolean {
  if (raw === null) return false;
  const value = raw.trim().toLowerCase();
  return value !== '0' && value !== 'false' && value !== 'no';
}
